import React, { useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiEye,
  FiFileText,
  FiRefreshCw,
  FiSlash,
  FiTrendingUp,
} from "react-icons/fi";

import "../ValidacaoAporte/ValidacaoAporte.css";
import { obterOperacaoFinanceira } from "./functions";

import Footer from "../../../components/Footer/Footer";
import Navbar from "../../../components/Navbar/Navbar";
import { LoadingButton } from "../../../components/loadingButtom";
import { ModalAprovar } from "../../../components/modais/ModalAprovar";
import { ModalComprovante } from "../../../components/modais/ModalComprovante";
import { ModalDesconectar } from "../../../components/modais/ModalDesconectar";
import { SelectServicoFinanceiro } from "../../../components/select/SelectServicoFinanceiro";
import {
  handleStatus,
  handleStatusAprovarAportePrivate,
} from "../../../components/Tabelas/ValidacaoAporte/functions";

const OPERACAO_FINANCEIRA = "Aporte";
const STATUS_OPERACAO_FINANCEIRA = "EmEspera";
const STATUS_OPERACAO_LABEL = "Em espera";

export interface OperacoesFinanceiras {
  dataSolicitacao: Date;
  valor: number;
  tipo: "Aporte" | "Saque";
  status: "EmEspera" | "Efetuado" | "Rejeitado";
  urlImagemComprovante?: string;
}

export interface Conta {
  servicoFinanceiroConta: string;
  operacoesFinanceiras: Array<OperacoesFinanceiras>;
}

export interface RespostaOperacaoFinanceira {
  idCredenciais: string;
  nome: string;
  conta: Conta;
}

interface OperacaoPendente extends OperacoesFinanceiras {
  idCredenciais: string;
  nome: string;
  servicoFinanceiroConta: string;
}

interface AporteValidationItemProps {
  operacao: OperacaoPendente;
  idServicoFinanceiro: number;
  setModalDesconectar: React.Dispatch<React.SetStateAction<boolean>>;
  setReload: React.Dispatch<React.SetStateAction<boolean>>;
}

const formatCurrency = (value: number, idServicoFinanceiro: number) =>
  new Intl.NumberFormat(idServicoFinanceiro === 3 ? "en-US" : "pt-BR", {
    style: "currency",
    currency: idServicoFinanceiro === 3 ? "USD" : "BRL",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatDate = (value: Date) =>
  new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));

const AporteValidationItem = ({
  operacao,
  idServicoFinanceiro,
  setModalDesconectar,
  setReload,
}: AporteValidationItemProps) => {
  const [loadingAprovar, setLoadingAprovar] = useState<boolean>(false);
  const [modalAprovar, setModalAprovar] = useState<boolean>(false);
  const [erroAprovar, setErroAprovar] = useState<boolean>(false);
  const [comprovanteModal, setComprovanteModal] = useState<boolean>(false);
  const temComprovante = Boolean(operacao.urlImagemComprovante);

  const aprovarOperacao = () => {
    setErroAprovar(false);

    if (idServicoFinanceiro !== 3) {
      setModalAprovar(true);
      return;
    }

    handleStatusAprovarAportePrivate(
      setLoadingAprovar,
      operacao.idCredenciais,
      idServicoFinanceiro,
      operacao.dataSolicitacao,
      "Efetuado",
      operacao.valor,
      operacao.tipo,
      0,
      "",
      setReload,
      setErroAprovar
    );
  };

  const rejeitarOperacao = () => {
    setErroAprovar(false);
    handleStatus(
      operacao.idCredenciais,
      operacao.dataSolicitacao,
      "Rejeitado",
      operacao.valor,
      operacao.tipo,
      idServicoFinanceiro,
      setLoadingAprovar,
      setErroAprovar,
      setReload,
      setModalDesconectar
    );
  };

  return (
    <article className="validacao-aporte-item">
      <div className="validacao-aporte-receipt">
        <button
          type="button"
          onClick={() => setComprovanteModal(true)}
          disabled={!temComprovante}
          aria-label={
            temComprovante
              ? `Ver comprovante de ${operacao.nome}`
              : `Comprovante indisponível para ${operacao.nome}`
          }
        >
          {temComprovante ? <FiEye /> : <FiFileText />}
        </button>
        <span>{temComprovante ? "Comprovante" : "Sem anexo"}</span>
      </div>

      <div className="validacao-aporte-client">
        <strong>{operacao.nome}</strong>
        <span>{operacao.servicoFinanceiroConta || "Serviço financeiro"}</span>
      </div>

      <div className="validacao-aporte-date">
        <span>Solicitado em</span>
        <strong>{formatDate(operacao.dataSolicitacao)}</strong>
      </div>

      <div className="validacao-aporte-value">
        <span>Valor</span>
        <strong>{formatCurrency(operacao.valor, idServicoFinanceiro)}</strong>
      </div>

      <div className="validacao-aporte-status">
        <span>
          <FiClock />
          Aguardando
        </span>
      </div>

      <div className="validacao-aporte-actions">
        {loadingAprovar ? (
          <LoadingButton />
        ) : (
          <>
            <button type="button" className="approve" onClick={aprovarOperacao}>
              <FiCheckCircle />
              Aprovar
            </button>
            <button type="button" className="reject" onClick={rejeitarOperacao}>
              <FiSlash />
              Reprovar
            </button>
          </>
        )}
        {erroAprovar && (
          <small>Não foi possível atualizar a operação. Tente novamente.</small>
        )}
      </div>

      {modalAprovar && (
        <ModalAprovar
          modal={modalAprovar}
          setModal={setModalAprovar}
          setModalDesconectar={setModalDesconectar}
          IdServicoFinanceiro={idServicoFinanceiro}
          setReload={setReload}
          setErroAprovar={setErroAprovar}
          dataSolicitacao={operacao.dataSolicitacao}
          idCredenciaisCliente={operacao.idCredenciais}
          status="Efetuado"
          tipo={operacao.tipo}
          valor={operacao.valor}
        />
      )}

      {comprovanteModal && (
        <ModalComprovante
          modal={comprovanteModal}
          setModal={setComprovanteModal}
          comprovanteBase64={operacao.urlImagemComprovante || ""}
        />
      )}
    </article>
  );
};

export const ValidacaoAporte = () => {
  const [modalDesconectar, setModalDesconectar] = useState<boolean>(false);
  const [data, setData] = useState<Array<RespostaOperacaoFinanceira>>([]);
  const [idServicoFinanceiro, setIdServicoFinanceiro] = useState<number>(1);
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [erroPage, setErroPage] = useState<boolean>(false);
  const [reloadPage, setReloadPage] = useState<boolean>(false);

  useEffect(() => {
    setLoadingPage(true);
    obterOperacaoFinanceira(
      idServicoFinanceiro,
      OPERACAO_FINANCEIRA,
      STATUS_OPERACAO_FINANCEIRA,
      setData,
      setLoadingPage,
      setErroPage,
      setModalDesconectar
    );
  }, [reloadPage, idServicoFinanceiro]);

  const operacoesPendentes = useMemo(
    () =>
      data.flatMap((cliente) =>
        cliente.conta.operacoesFinanceiras.map((operacao) => ({
          ...operacao,
          idCredenciais: cliente.idCredenciais,
          nome: cliente.nome,
          servicoFinanceiroConta: cliente.conta.servicoFinanceiroConta,
        }))
      ),
    [data]
  );

  const totalPendente = useMemo(
    () =>
      operacoesPendentes.reduce(
        (total, operacao) => total + Number(operacao.valor || 0),
        0
      ),
    [operacoesPendentes]
  );

  const totalComprovantes = operacoesPendentes.filter(
    (operacao) => operacao.urlImagemComprovante
  ).length;

  const serviceLabel =
    idServicoFinanceiro === 3
      ? "Private"
      : idServicoFinanceiro === 2
        ? "Custódia"
        : "Assessoria";

  return (
    <>
      <Navbar />
      <main className="validacao-aporte-page">
        <section className="validacao-aporte-hero">
          <div>
            <span>Validação financeira</span>
            <h1>Aportes pendentes</h1>
            <p>
              Analise comprovantes, aprove lançamentos e mantenha a operação de
              capital da Omex Capital com rastreabilidade.
            </p>
          </div>

          <div className="validacao-aporte-filter">
            <label>
              <span>Serviço financeiro</span>
              <SelectServicoFinanceiro
                className="validacao-aporte-select"
                setServico={setIdServicoFinanceiro}
              />
            </label>
            <button
              type="button"
              onClick={() => setReloadPage((value) => !value)}
              disabled={loadingPage}
            >
              <FiRefreshCw />
              Atualizar
            </button>
          </div>
        </section>

        <section className="validacao-aporte-summary" aria-label="Resumo de aportes pendentes">
          <article>
            <FiTrendingUp />
            <span>Fila pendente</span>
            <strong>{operacoesPendentes.length}</strong>
          </article>
          <article>
            <FiDollarSign />
            <span>Valor em análise</span>
            <strong>{formatCurrency(totalPendente, idServicoFinanceiro)}</strong>
          </article>
          <article>
            <FiFileText />
            <span>Com comprovante</span>
            <strong>{totalComprovantes}</strong>
          </article>
          <article>
            <FiClock />
            <span>Serviço ativo</span>
            <strong>{serviceLabel}</strong>
          </article>
        </section>

        <section className="validacao-aporte-panel">
          <div className="validacao-aporte-panel-header">
            <div>
              <span>Aguardando validação</span>
              <h2>Solicitações de aporte</h2>
            </div>
            <strong>{STATUS_OPERACAO_LABEL}</strong>
          </div>

          {erroPage ? (
            <div className="validacao-aporte-state error">
              <FiAlertCircle />
              <strong>Não foi possível carregar as solicitações.</strong>
              <button type="button" onClick={() => setReloadPage((value) => !value)}>
                <FiRefreshCw />
                Tentar novamente
              </button>
            </div>
          ) : loadingPage ? (
            <div className="validacao-aporte-state">
              <LoadingButton />
            </div>
          ) : operacoesPendentes.length ? (
            <div className="validacao-aporte-list">
              <div className="validacao-aporte-list-head" aria-hidden="true">
                <span>Comprovante</span>
                <span>Cliente</span>
                <span>Data</span>
                <span>Valor</span>
                <span>Status</span>
                <span>Ações</span>
              </div>
              {operacoesPendentes.map((operacao) => (
                <AporteValidationItem
                  key={`${operacao.idCredenciais}-${operacao.dataSolicitacao}-${operacao.valor}`}
                  operacao={operacao}
                  idServicoFinanceiro={idServicoFinanceiro}
                  setModalDesconectar={setModalDesconectar}
                  setReload={setReloadPage}
                />
              ))}
            </div>
          ) : (
            <div className="validacao-aporte-state">
              <FiCheckCircle />
              <strong>Nenhum aporte pendente no momento.</strong>
              <p>A fila de validação está limpa para o serviço selecionado.</p>
            </div>
          )}
        </section>
      </main>

      {modalDesconectar && <ModalDesconectar modal={modalDesconectar} />}
      <Footer />
    </>
  );
};
